import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ClipboardList } from "lucide-react";
import { useTranslation } from "../services/translation.service";
import { useTasks, getTaskStatusLabel } from "../context/TasksContext";
import { useCart } from "../context/CartContext";
import { OpenTask } from "../services/tasks.service";

interface OpenTasksPopupProps {
  isOpen: boolean;
  onClose: () => void;
}

function formatDate(dateStr: string): string {
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return dateStr;
  const day = String(d.getDate()).padStart(2, "0");
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const year = d.getFullYear();
  const hours = String(d.getHours()).padStart(2, "0");
  const minutes = String(d.getMinutes()).padStart(2, "0");
  return `${day}/${month}/${year} ${hours}:${minutes}`;
}

const OpenTasksPopup: React.FC<OpenTasksPopupProps> = ({ isOpen, onClose }) => {
  const { t } = useTranslation();
  const { tasks, tasksLoading } = useTasks();
  const { businessInfo } = useCart();

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0, y: -8, scale: 0.96 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: -8, scale: 0.96 }}
          transition={{ duration: 0.18 }}
          className="fixed inset-x-4 top-20 md:absolute md:inset-x-auto md:end-0 md:top-full md:mt-2 md:w-80 bg-white rounded-xl border border-gray-200 shadow-lg z-[130]"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
            <div className="flex flex-col gap-0.5">
              {businessInfo?.businessName && (
                <span className="text-xs text-gray-500 truncate max-w-[180px]">
                  {businessInfo.businessName}
                </span>
              )}
              <div className="flex items-center gap-2">
                <ClipboardList className="w-4 h-4 text-gray-500" />
                <span className="text-sm font-semibold text-gray-800">
                  {t("tasks.popupTitle")}
                </span>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-1 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
              aria-label="סגור"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Body */}
          <div className="max-h-72 overflow-y-auto">
            {tasksLoading ? (
              <div className="px-4 py-6 text-center text-sm text-gray-400">
                ...
              </div>
            ) : tasks.length === 0 ? (
              <div className="px-4 py-6 text-center text-sm text-gray-400">
                {t("tasks.noTasks")}
              </div>
            ) : (
              <ul className="divide-y divide-gray-100">
                {tasks.map((task: OpenTask) => (
                  <li key={task.id} className="px-4 py-3">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex flex-col gap-0.5 min-w-0">
                        <span className="text-xs text-gray-500">
                          {formatDate(task.createdDateTime)}
                        </span>
                        <span className="text-xs font-medium text-gray-700">
                          #{task.id}
                        </span>
                      </div>
                      <span
                        className={`flex-shrink-0 text-xs font-medium px-2 py-0.5 rounded-full ${
                          task.taskStatus.statusName.toLowerCase() === "new"
                            ? "bg-amber-50 text-amber-700"
                            : "bg-blue-50 text-blue-700"
                        }`}
                      >
                        {getTaskStatusLabel(
                          task.taskStatus.statusName,
                          t as any,
                        )}
                      </span>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default OpenTasksPopup;
